import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash, FaArrowLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Search from "../../../../common/Search";
import {
  ItemList,
  DeleteItem,
  ItemChangeStatus,
} from "../../../../redux/services/ContentService/content.Services";
import Pagination from "../../../../common/Pagination";
import { successToast, errorToast } from "../../../../Utils/toast";

function Itemlist() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  useEffect(() => {
    const storedSection = localStorage.getItem("selectedSection");
    const storedSolution = localStorage.getItem("selectedSolution");
    const storedModule = localStorage.getItem("selectedModule");

    if (storedSection) setSelectedSection(JSON.parse(storedSection));
    if (storedSolution) setSelectedSolution(JSON.parse(storedSolution));
    if (storedModule) setSelectedModule(JSON.parse(storedModule));
  }, []);

  const sectionId = selectedSection?.id;

  const [totalRecords, setTotalRecords] = useState(0);
  const [itemlist, setItemList] = useState([]);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const refreshData = useSelector((state) => state.productModal.refreshData);

  const fetchItem = async () => {
    if (!sectionId) return;
    try {
      setLoading(true);
      const payload = { page, search, limit, section_id: sectionId };
      const res = await ItemList(payload);
      setItemList(res?.data || []);
      setLastPage(res?.last_page || 1);
      setTotalRecords(res?.total || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line
  }, [page, search, sectionId, refreshData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) setPage(newPage);
  };

  const handleAddItem = () => {
    navigate("/add-item");
  };

  const handleViewItem = (item) => {
    localStorage.setItem("viewItem", JSON.stringify(item));
    navigate("/view-item");
  };

  const handleEditItem = (item) => {
    localStorage.setItem("editItem", JSON.stringify(item));
    navigate("/edit-item");
  };

  const handleDeleteItem = async (item) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${item.title}"?`
    );
    if (!confirmDelete) return;

    try {
      const res = await DeleteItem({ id: item.id });

      if (res?.success) {
        successToast(res?.message || "Item deleted successfully!");
        fetchItem();
      } else {
        errorToast(res?.message || "Failed to delete item");
      }
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || "Something went wrong");
    }
  };

  const handleToggleStatus = async (item) => {
    const confirmToggle = window.confirm(`Change status of "${item.title}"?`);
    if (!confirmToggle) return;

    try {
      const newStatus = item.status == 1 ? 0 : 1;
      const res = await ItemChangeStatus({
        id: item.id,
        status: newStatus,
      });

      if (res?.success) {
        successToast(res?.message || "Status changed successfully!");
        fetchItem();
      } else {
        errorToast(res?.message || "Failed to change status");
      }
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div>
      <div className="d-md-flex d-block align-items-center justify-content-between mt-1 page-header-breadcrumb">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="page-header">
            <div>
              <h3 className="page-title mb-0">
                Item List
                {selectedSection && (
                  <span className="text-muted ms-2">
                    — {selectedSection.section_key}
                  </span>
                )}
              </h3>
              <nav className="page-breadcrumb">
                <Link to="/">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/module-list">Modules</Link>
                <span className="mx-2">/</span>
                {selectedModule && (
                  <>
                    <span>{selectedModule.name}</span>
                    <span className="mx-2">/</span>
                  </>
                )}
                <Link to="/solutions-list">Solutions</Link>
                <span className="mx-2">/</span>
                <Link to="/sections-list">Sections</Link>
                <span className="mx-2">/</span>
                <span>Items</span>
              </nav>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button className="add-btn" onClick={handleAddItem}>
              + Add Item
            </button>

            <Link
              to="/sections-list"
              className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px", borderRadius: "8px" }}
            >
              <FaArrowLeft />
            </Link>
          </div>
        </div>
      </div>

      <div className="main_page">
        {!sectionId && (
          <div className="alert alert-warning mx-2">
            ⚠️ No section selected. Please go back and select a section.
          </div>
        )}

        {(totalRecords > 0 || search) && (
          <div className="d-flex justify-content-end align-items-center mx-2 mb-2">
            <Search
              search={search}
              placeholder="Search Item..."
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          </div>
        )}

        <div className="st-wrapper-card text-center">
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Icon</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Change Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {itemlist.length > 0 ? (
                  itemlist.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1 + (page - 1) * limit}</td>
                      <td>
                        {item.icon && (
                          <i
                            className={`fas ${item.icon} fa-lg text-primary`}
                          ></i>
                        )}
                      </td>
                      <td>{item.title}</td>
                      <td>
                        {item.description
                          ? item.description.substring(0, 50) + "..."
                          : "-"}
                      </td>
                      <td>
                        <div className="city-status-switch">
                          <input
                            type="checkbox"
                            id={`status-${item.id}`}
                            className="city-status-input"
                            checked={item.status == 1}
                            onChange={() => handleToggleStatus(item)}
                          />
                          <label
                            htmlFor={`status-${item.id}`}
                            className="city-status-label"
                          >
                            <span className="city-status-ball"></span>
                            <span className="city-status-text">
                              {item.status == 1 ? "Active" : "Inactive"}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <button
                            className="st-action-btn st-view-btn"
                            onClick={() => handleViewItem(item)}
                          >
                            <FaEye />
                          </button>

                          <button
                            className="st-action-btn st-edit-btn"
                            onClick={() => handleEditItem(item)}
                          >
                            <FaEdit />
                          </button>

                          <button
                            className="st-action-btn st-delete-btn"
                            onClick={() => handleDeleteItem(item)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <h5 className="mb-0">
                        {!sectionId ? "No section selected" : "No Data Found"}
                      </h5>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {lastPage > 1 && (
              <div className="d-flex justify-content-end mt-3">
                <Pagination
                  page={page}
                  totalPages={lastPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Itemlist;